<template>
  <nav class="sidebar-nav app-sidebar-nav">
    <VuePerfectScrollbar class="scroll-area" :settings="psSettings">
      <ul class="nav">
        <li
          v-if="hasCustomerBrand"
          class="sidebar-customer-brand"
          :class="{ 'sidebar-customer-brand--mobile': isMobileViewport }"
        >
          <CustomerBrandLogo :variant="sidebarBrandVariant" context="sidebar" :alt="projectName" />
        </li>
        <template v-for="(item, index) in navItems">
          <SidebarNavTitle
            v-if="item.title"
            :key="'title-' + index"
            :name="item.name"
            :classes="item.class"
            :wrapper="item.wrapper"
          />
          <SidebarNavDivider
            v-else-if="item.divider"
            :key="'divider-' + index"
            :classes="item.class"
          />
          <AppSidebarNavLabel
            v-else-if="item.label"
            :key="'label-' + index"
            :name="item.name"
            :url="item.url"
            :icon="item.icon"
            :label="item.label"
            :classes="item.class"
          />
          <AppSidebarNavDropdown
            v-else-if="item.children"
            :key="'dropdown-' + index"
            :name="item.name"
            :url="item.url"
            :icon="item.icon"
          >
            <template v-for="(childL1, index1) in item.children">
              <AppSidebarNavDropdown
                v-if="childL1.children"
                :key="'dropdown-' + index + '-' + index1"
                :name="childL1.name"
                :url="childL1.url"
                :icon="childL1.icon"
              >
                <li
                  v-for="(childL2, index2) in childL1.children"
                  :key="'item-' + index + '-' + index1 + '-' + index2"
                  class="nav-item"
                >
                  <AppSidebarNavLink
                    :name="childL2.name"
                    :url="childL2.url"
                    :icon="childL2.icon"
                    :badge="childL2.badge"
                    :variant="childL2.variant"
                    :attributes="childL2.attributes"
                  />
                </li>
              </AppSidebarNavDropdown>
              <SidebarNavItem
                v-else
                :key="'item-' + index + '-' + index1"
                :classes="item.class"
              >
                <AppSidebarNavLink
                  :name="childL1.name"
                  :url="childL1.url"
                  :icon="childL1.icon"
                  :badge="childL1.badge"
                  :variant="childL1.variant"
                  :attributes="childL1.attributes"
                />
              </SidebarNavItem>
            </template>
          </AppSidebarNavDropdown>
          <SidebarNavItem
            v-else
            :key="'item-' + index"
            :classes="item.class"
          >
            <AppSidebarNavLink
              :name="item.name"
              :url="item.url"
              :icon="item.icon"
              :badge="item.badge"
              :variant="item.variant"
              :attributes="item.attributes"
            />
          </SidebarNavItem>
        </template>
      </ul>
      <slot></slot>
    </VuePerfectScrollbar>
  </nav>
</template>

<script>
import {
  SidebarNavDivider,
  SidebarNavItem,
  SidebarNavTitle,
} from '@coreui/vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import AppSidebarNavDropdown from './AppSidebarNavDropdown'
import AppSidebarNavLabel from './AppSidebarNavLabel'
import AppSidebarNavLink from './AppSidebarNavLink'
import Session from '@/handler/session'
import Branding from '@/components/branding/branding'
import CustomerBrandLogo from '@/components/branding/CustomerBrandLogo'
import { EventBus } from '@/handler/event-bus'

export default {
  name: 'AppSidebarNav',
  components: {
    AppSidebarNavDropdown,
    AppSidebarNavLabel,
    AppSidebarNavLink,
    CustomerBrandLogo,
    SidebarNavDivider,
    SidebarNavItem,
    SidebarNavTitle,
    VuePerfectScrollbar,
  },
  props: {
    navItems: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      brandingVersion: 0,
      isSidebarMinimized: false,
      isMobileViewport: false,
      bodyClassObserver: null,
    }
  },
  created() {
    EventBus.$on('Settings:settings', this.refreshBranding)
  },
  mounted() {
    this.refreshSidebarState()
    window.addEventListener('resize', this.refreshSidebarState)

    if (typeof MutationObserver !== 'undefined' && document && document.body) {
      this.bodyClassObserver = new MutationObserver(this.refreshSidebarState)
      this.bodyClassObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class'],
      })
    }
  },
  beforeDestroy() {
    EventBus.$off('Settings:settings', this.refreshBranding)
    if (this.bodyClassObserver) {
      this.bodyClassObserver.disconnect()
      this.bodyClassObserver = null
    }
    window.removeEventListener('resize', this.refreshSidebarState)
  },
  computed: {
    projectName() {
      const session = Session.getSession()
      return session && session.settings
        ? session.settings.proyect_name || 'Empresa'
        : 'Empresa'
    },
    hasCustomerBrand() {
      this.brandingVersion
      const session = Session.getSession()
      const branding = Branding.resolve(session && session.settings)

      if (this.isSidebarMinimized && !this.isMobileViewport) {
        return branding.iconCustomized && branding.icon
      }

      return (branding.logoCustomized && branding.logo) ||
             (branding.iconCustomized && branding.icon)
    },
    sidebarBrandVariant() {
      return this.isSidebarMinimized && !this.isMobileViewport ? 'compact' : 'full'
    },
    psSettings() {
      return {
        maxScrollbarLength: 200,
        minScrollbarLength: 40,
        suppressScrollX: getComputedStyle(document.querySelector('html')).direction !== 'rtl',
        wheelPropagation: false,
        interceptRailY: styles => ({ ...styles, height: 0 }),
      }
    },
  },
  methods: {
    refreshBranding() {
      this.brandingVersion++
    },
    refreshSidebarState() {
      this.isMobileViewport = window.innerWidth < 992
      const sidebar = document && document.querySelector
        ? document.querySelector('.sidebar')
        : null
      const sidebarLooksMinimized = sidebar && sidebar.getBoundingClientRect().width <= 80
      const hasMinimizedClass = document &&
        document.body &&
        document.body.classList.contains('sidebar-minimized')

      this.isSidebarMinimized = !!(hasMinimizedClass || sidebarLooksMinimized)
    },
  },
}
</script>

<style>
.scroll-area {
  position: absolute;
  height: 100%;
  margin: auto;
}

.sidebar-customer-brand {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  margin: 4px 10px 8px;
  padding: 0;
}

.sidebar-minimized .sidebar-customer-brand {
  position: relative;
  left: 0;
  z-index: 2;
  flex: 0 0 50px;
  width: 50px;
  max-width: 50px;
  overflow: hidden;
  min-height: 42px;
  margin: 4px 0 8px;
  padding: 0;
  pointer-events: none;
  background: var(--app-color-sidebar, #0b1f3b);
}

.sidebar-minimized .sidebar-customer-brand .customer-brand-logo {
  width: 36px;
  max-width: 36px;
  transform: none;
}

@media (max-width: 991.98px) {
  .sidebar-minimized .sidebar-customer-brand.sidebar-customer-brand--mobile {
    display: flex;
    flex: 0 1 auto;
    width: auto;
    max-width: none;
    min-height: 50px;
    margin: 4px 10px 8px;
    pointer-events: auto;
    background: transparent;
  }

  .sidebar-minimized .sidebar-customer-brand.sidebar-customer-brand--mobile .customer-brand-logo {
    width: 100%;
    max-width: none;
  }
}
</style>
